import axios from "axios";
import { getTabContextId } from "./tabContext";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "/api",
  timeout: 30000,
  withCredentials: true,
});

// Prevent repeated logout alerts
let isSessionExpiredShown = false;
let csrfToken = "";

export function resetSessionExpiredGuard() {
  isSessionExpiredShown = false;
}

export function setCsrfToken(nextToken) {
  csrfToken = String(nextToken || "").trim();
}

export function clearCsrfToken() {
  csrfToken = "";
}

export function getSessionRequestHeaders({ includeCsrf = false } = {}) {
  const headers = {
    "X-Tab-Context-Id": getTabContextId(),
  };

  if (includeCsrf && csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }

  return headers;
}

function notifySessionExpiry(response) {
  const sessionExpiresAt = response?.headers?.["x-session-expires-at"];
  if (!sessionExpiresAt || typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("auth-session-extended", {
      detail: { session_expires_at: sessionExpiresAt },
    })
  );
}

function updateCsrfTokenFromResponse(response) {
  const nextToken =
    response?.headers?.["x-csrf-token"] ||
    (response?.data && typeof response.data === "object" ? response.data.csrf_token : "");
  if (nextToken) setCsrfToken(nextToken);
}

function isUnsafeMethod(method) {
  return !["get", "head", "options"].includes(String(method || "get").toLowerCase());
}

API.interceptors.request.use(
  (config) => {
    const isFormData =
      typeof FormData !== "undefined" && config.data instanceof FormData;

    if (isFormData) {
      delete config.headers["Content-Type"];
    } else {
      config.headers["Content-Type"] =
        config.headers["Content-Type"] || "application/json";
    }

    config.headers["X-Tab-Context-Id"] = getTabContextId();

    if (isUnsafeMethod(config.method) && csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    } else if (!csrfToken) {
      delete config.headers["X-CSRF-Token"];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

API.interceptors.response.use(
  (response) => {
    notifySessionExpiry(response);
    updateCsrfTokenFromResponse(response);
    return response;
  },
  async (error) => {
    updateCsrfTokenFromResponse(error.response);
    let status = error.response?.status;

    if (status === 401 && !error.config?.skipSessionExpired) {
      clearCsrfToken();
      if (!isSessionExpiredShown) {
        isSessionExpiredShown = true;

        const message =
          error.response?.data?.message ||
          "Your session has expired. Please login again.";

        setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("session-expired", {
              detail: { message },
            })
          );
        }, 50);
      }
    }

    if (status === 403) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "You do not have permission to perform this action.";

      console.warn("Access denied:", message);
    }

    return Promise.reject(error);
  }
);

export default API;
